import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as BaseEntityLinkProps } from "~/components/base-entity-link";
import {
  HeaderAllMenuItemLink,
  HeaderAllMenuItemLinkContainer,
  HeaderAllMenuTitle,
} from "~/components/header/header-oneline-menu-container/header-all-menu/common";
import { useOnelineMenuTracking } from "~/components/header/header-oneline-menu-container/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { useEditionValue } from "~/lib/edition";
import type { headerAllMenuTopicsQuery$key } from "~/queries/__generated__/headerAllMenuTopicsQuery.graphql";

import { Container } from "./styles";

export type Props = {
  className?: string;
  queryParameter: BaseEntityLinkProps["query"];
  reference: headerAllMenuTopicsQuery$key;
};

export const HeaderAllMenuTopics: FunctionComponent<Props> = ({
  className,
  queryParameter,
  reference: reference_,
}) => {
  const queue = useFragment(
    graphql`
      fragment headerAllMenuTopicsQuery on Query {
        topicQueue: queue(filter: { name: $topicQueueName }) {
          items(first: 6) {
            edges {
              node {
                ... on Topic {
                  ...entityLink
                  name
                }
              }
            }
          }
        }
      }
    `,
    reference_,
  );

  const { edition: UATrackingEdition } = useOnelineMenuTracking();
  const edition = useEditionValue();

  return (
    <Container className={className}>
      <HeaderAllMenuTitle>Topics</HeaderAllMenuTitle>
      <HeaderAllMenuItemLinkContainer>
        {queue.topicQueue?.items?.edges.map((edge, index) => (
          <HeaderAllMenuItemLink
            key={index}
            query={queryParameter}
            reference={edge.node}
            onClick={() => {
              sendGA4Tracking({
                action: "click",
                category: "menu",
                customized_parameters: {
                  action_type: "redirect",
                  edition,
                  menu_type: "mega",
                  topic: edge.node.name ?? "",
                },
                subcategory: "section",
              });
              sendGATracking({
                action: "Linemenu/Click",
                category: "Menu",
                label: `${UATrackingEdition}/Linemenu/All/Topics/${edge.node.name ?? ""}`,
              });
            }}
          >
            {edge.node.name}
          </HeaderAllMenuItemLink>
        ))}
      </HeaderAllMenuItemLinkContainer>
    </Container>
  );
};

HeaderAllMenuTopics.displayName = "HeaderAllMenuTopics";
